import { renderDonutForPdf, renderBarForPdf, renderDualAxesForPdf, drawChartRow, drawChartSection } from "../pdfCharts";
import { BAR_PALETTE, DONUT_COLORS } from "../../../../utils/chartConfig";
import { formatWhole } from "../../../../utils/numberFormat";
import { getAnnualTotalsByGroup, buildFuelsBarChartData, buildFuelsBarChartDataCost, fuelsGroupLabels, FUELS_GROUP_LABEL_KEYS } from "./TableGraf";
import { buildFuelsComparisonData, buildFuelsIntensityData, buildSharedYScale } from "./fuelsChartData";

// "Enero" -> "Ene", etc. — con los gráficos compartiendo página el nombre
// completo del mes no entra sin superponerse (mismo ajuste que en Energía).
const monthLabel3 = (v) => String(v).slice(0, 3);

// Arma los 3 gráficos de TableGraf.js (dona de fuentes + barra de consumo +
// barra de costo) como SVG vectorial para el PDF — misma lógica de datos
// que ya usa TableGraf.js en pantalla, reutilizada tal cual.
export async function renderFuelsChartsForPdf(totales, filters, t) {
  const dataConsumo = getAnnualTotalsByGroup(totales, filters?.year);
  const donutData = Object.entries(dataConsumo)
    .filter(([, { total }]) => total > 0)
    .map(([groupKey, { total }]) => {
      const type = fuelsGroupLabels[groupKey];
      return { type, value: total, label: t(FUELS_GROUP_LABEL_KEYS[type] || type) };
    });

  const [donut, barConsumo, barCosto] = await Promise.all([
    renderDonutForPdf(donutData, DONUT_COLORS.fuels),
    // Regla 1 (AJUSTES DE MAGNITUDES NUMÉRICAS): espacio fino separador de
    // miles, sin decimales — mismo criterio que ya tienen las 2 secciones
    // DualAxes de más abajo.
    renderBarForPdf(buildFuelsBarChartData(totales), "MWhe", BAR_PALETTE, { yFormatter: formatWhole }),
    renderBarForPdf(buildFuelsBarChartDataCost(totales), t("cost"), BAR_PALETTE, { yFormatter: formatWhole }),
  ]);

  return { donut, barConsumo, barCosto };
}

// "Comparación 3 años" — barras de consumo mensual por año + línea punteada
// de promedio, para el combustible que estaba seleccionado en pantalla al
// exportar (un PDF no es interactivo, no tiene sentido el selector).
export async function renderFuelsComparisonForPdf(resultsByYear, t, selectedFuel) {
  const { years, fuels, chartData, averageData, yearColorMap } = buildFuelsComparisonData(resultsByYear, t, selectedFuel);
  const fuelLabel = fuels.find((f) => f.key === selectedFuel)?.label || "";
  const sharedYScale = buildSharedYScale(chartData, averageData);

  const children = [
    {
      data: chartData,
      type: "interval",
      yField: "value",
      colorField: "year",
      group: true,
      style: { maxWidth: 40 },
      scale: { color: ({ year }) => yearColorMap[year], y: sharedYScale },
    },
    {
      data: averageData,
      type: "line",
      yField: "value",
      colorField: "year",
      axis: { y: false },
      scale: { y: sharedYScale },
      style: { lineWidth: 2, lineDash: [4, 8] },
    },
  ];

  const result = await renderDualAxesForPdf(children, {
    xField: "month",
    scale: {
      color: { domain: Object.keys(yearColorMap), range: Object.values(yearColorMap) },
      x: { paddingOuter: 0.1 },
    },
    // En pantalla el título del eje Y está en false — el usuario pidió que
    // en el PDF sí se vea. El de X ("Mes") se sacó: ya se entiende con los
    // nombres de mes abreviados (Ene, Feb, ...) en las etiquetas.
    axis: {
      x: { title: false, labelAutoHide: false, labelAutoRotate: false, labelFill: "#000", labelFontSize: 8, labelFormatter: monthLabel3 },
      y: { title: "kWhe", grid: true, labelFill: "#000", labelFontSize: 8, labelFormatter: (v) => formatWhole(v) },
    },
  });
  if (result.isEmpty) return result;

  const legendItems = years.map((year) => ({ label: String(year), color: yearColorMap[year] }));
  return { ...result, legendItems, fuelLabel };
}

// "Intensidad por tipo" — consumo/vehículo mensual (línea sólida) +
// promedio (línea punteada), para el combustible seleccionado en pantalla.
export async function renderFuelsIntensityForPdf(resultsByYear, t, selectedFuel) {
  const { years, fuelTypes, intensityData, averageData, yearColorMap } = buildFuelsIntensityData(resultsByYear, t, selectedFuel);
  const fuelLabel = fuelTypes[selectedFuel]?.label || "";
  const sharedYScale = buildSharedYScale(intensityData, averageData);

  const children = [
    {
      data: intensityData,
      type: "line",
      yField: "value",
      colorField: "year",
      style: { lineWidth: 4 },
      scale: { y: sharedYScale },
      connectNulls: false,
    },
    {
      data: averageData,
      type: "line",
      yField: "value",
      colorField: "year",
      axis: { y: false },
      scale: { y: sharedYScale },
      style: { lineWidth: 2, lineDash: [4, 8] },
      connectNulls: false,
    },
  ];

  const result = await renderDualAxesForPdf(children, {
    xField: "month",
    scale: { color: { domain: Object.keys(yearColorMap), range: Object.values(yearColorMap) } },
    // "kWhe / vehículo" ya se ve en pantalla en el eje Y. El de X ("Mes")
    // no hace falta: ya se entiende con los nombres de mes abreviados.
    axis: {
      x: { title: false, labelFill: "#000", labelFontSize: 8, labelFormatter: monthLabel3 },
      y: { title: "kWhe / vehículo", labelFill: "#000", labelFontSize: 8, labelFormatter: (v) => formatWhole(v) },
    },
  });
  if (result.isEmpty) return result;

  const legendItems = years.map((year) => ({ label: String(year), color: yearColorMap[year] }));
  return { ...result, legendItems, fuelLabel };
}

// Orquesta el dibujado de los 5 gráficos de Combustibles/Vehículos en el
// PDF: se pasa como `renderCustomCharts` a KPIExportIconButton. Mismo
// criterio de layout ya resuelto en EnergyKPIsDashboard/pdfReport.js: fila
// de 3 tarjetas (dona+2 barras) con altura fija, y las 2 secciones DualAxes
// repartiéndose lo que quede de la página (o una hoja nueva si no entran).
export async function drawFuelsCustomCharts(doc, ctx, { totales, filters, resultsByYear, comparisonFuel, intensityFuel, t }) {
  const { margin, pageWidth, pageHeight, imgWidth } = ctx;
  let yOffset = ctx.yOffset;

  const { donut, barConsumo, barCosto } = await renderFuelsChartsForPdf(totales, filters, t);
  const fuelsCards = [
    { title: t("fuel_sources"), ...donut },
    { title: t("fuel_consumption"), ...barConsumo },
    { title: t("fuel_costs"), ...barCosto },
  ];
  const CHART_ROW_H = 210;
  const remaining = pageHeight - margin - yOffset;
  if (CHART_ROW_H > remaining) {
    doc.addPage();
    yOffset = margin;
  }
  await drawChartRow(doc, fuelsCards, { x: margin, y: yOffset, availW: imgWidth, cardH: CHART_ROW_H, emptyMessage: t("no_data_available") });
  yOffset += CHART_ROW_H + 10;
  donut.cleanup?.();
  barConsumo.cleanup?.();
  barCosto.cleanup?.();

  const GAP = 10;
  const MIN_BOTH_H = 480; // 240pt mínimo cada uno
  let availForBoth = pageHeight - margin - yOffset - GAP;
  if (availForBoth < MIN_BOTH_H) {
    doc.addPage();
    yOffset = margin;
    availForBoth = pageHeight - margin - yOffset - GAP;
  }
  const DUAL_AXES_ROW_H = Math.floor(availForBoth / 2);

  // Mismo texto que el título en pantalla (t(...)), más el combustible
  // mostrado — sin el selector interactivo, el PDF necesita decir qué
  // combustible es cada gráfico.
  const comparison = await renderFuelsComparisonForPdf(resultsByYear, t, comparisonFuel);
  const comparisonTitle = comparison.fuelLabel
    ? `${t("fuel_comparison_last_3_years")} — ${comparison.fuelLabel}`
    : t("fuel_comparison_last_3_years");
  await drawChartSection(doc, { x: margin, y: yOffset, width: imgWidth, height: DUAL_AXES_ROW_H, title: comparisonTitle, emptyMessage: t("no_data_available"), ...comparison });
  yOffset += DUAL_AXES_ROW_H + GAP;
  comparison.cleanup?.();

  const intensity = await renderFuelsIntensityForPdf(resultsByYear, t, intensityFuel);
  const intensityTitle = intensity.fuelLabel
    ? `${t("intensity_by_fuel_type")} — ${intensity.fuelLabel}`
    : t("intensity_by_fuel_type");
  await drawChartSection(doc, { x: margin, y: yOffset, width: imgWidth, height: DUAL_AXES_ROW_H, title: intensityTitle, emptyMessage: t("no_data_available"), ...intensity });
  yOffset += DUAL_AXES_ROW_H + GAP;
  intensity.cleanup?.();

  return yOffset;
}
